import React from "react";
import {Box, Container, Typography} from "@mui/material";
import ArrowOutwardRoundedIcon from "@mui/icons-material/ArrowOutwardRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import GuidedTour from "../shared/GuidedTour";
import FaqSearch from "../support/FaqSearch";
import UpgradeNotice from "../shared/UpgradeNotice";
import DashboardHeader from "./dashboard/DashboardHeader";
import DashboardGrid from "./dashboard/DashboardGrid";
import DashboardKpiCard from "./dashboard/DashboardKpiCard";
import DashboardDistributionCard from "./dashboard/DashboardDistributionCard";
import DashboardCashflowCard from "./dashboard/DashboardCashflowCard";
import DashboardDueTable from "./dashboard/DashboardDueTable";
import DashboardQuickLinks from "./dashboard/DashboardQuickLinks";
import DashboardSnackbar from "./dashboard/DashboardSnackbar";
import Sparkline from "./dashboard/Sparkline";
import useDashboardData from "./dashboard/useDashboardData";
import {
    fmtEUR,
    safeExpenseAmount,
    safeIncomeAmount,
    safeInvoiceTotal,
    STATIC_LAYOUTS,
} from "./dashboard/dashboardUtils";

export default function Dashboard() {
    const {
        hasAccounting,
        loading,
        err,
        toast,
        setToast,
        monthFilter,
        setMonthFilter,
        yearOptions,
        periodLabel,
        openInvoices,
        overdueInvoices,
        totalOpen,
        totalOverdue,
        incomeThisMonth,
        expenseThisMonth,
        incomeSeries,
        expenseSeries,
        cashflowSeries,
        last6,
        nextDue,
        chartData,
        invoices,
        incomes,
        expenses,
        handleRefresh,
        tourOpen,
        tourSteps,
        startTour,
        handleTourClose,
        handleTourComplete,
    } = useDashboardData();

    const items = {
        "kpi-open": (
            <DashboardKpiCard
                title="Offene Rechnungen"
                value={fmtEUR(totalOpen)}
                caption={`${openInvoices.length} offen`}
                icon={<ArrowOutwardRoundedIcon aria-hidden/>}
                to="/open-invoices"
            />
        ),
        "kpi-overdue": (
            <DashboardKpiCard
                title="Überfällig"
                value={fmtEUR(totalOverdue)}
                caption={`${overdueInvoices.length} überfällig`}
                icon={<WarningAmberRoundedIcon aria-hidden/>}
                to="/open-invoices?preset=overdue"
            />
        ),
        "kpi-income": hasAccounting ? (
            <DashboardKpiCard
                title={`Einnahmen (${periodLabel})`}
                value={fmtEUR(incomeThisMonth)}
                caption={<Sparkline data={incomes.map(safeIncomeAmount).slice(-12)}/>}
                icon={<ArrowOutwardRoundedIcon aria-hidden/>}
            />
        ) : (
            <UpgradeNotice size="compact" fullHeight title="Einnahmen freischalten" actionLabel="Zum 20 € Tarif">
                <Typography variant="caption" color="text.secondary">
                    Das Einnahmen-Modul ist im Buchhaltungstarif enthalten.
                </Typography>
            </UpgradeNotice>
        ),
        "kpi-expense": hasAccounting ? (
            <DashboardKpiCard
                title={`Ausgaben (${periodLabel})`}
                value={fmtEUR(expenseThisMonth)}
                caption={<Sparkline data={expenses.map(safeExpenseAmount).slice(-12)}/>}
                icon={<ArrowOutwardRoundedIcon aria-hidden/>}
            />
        ) : (
            <UpgradeNotice size="compact" fullHeight title="Ausgaben freischalten" actionLabel="Zum 20 € Tarif">
                <Typography variant="caption" color="text.secondary">
                    Aktiviere Buchhaltung für Ausgaben- und OCR-Workflow.
                </Typography>
            </UpgradeNotice>
        ),
        "chart-distribution": (
            <DashboardDistributionCard loading={loading} chartData={chartData} hasAccounting={hasAccounting}/>
        ),
        "chart-cashflow": (
            <DashboardCashflowCard
                loading={loading}
                hasAccounting={hasAccounting}
                last6={last6}
                cashflowSeries={cashflowSeries}
                incomeSeries={incomeSeries}
                expenseSeries={expenseSeries}
                fmtEUR={fmtEUR}
            />
        ),
        "table-due": (
            <DashboardDueTable
                loading={loading}
                nextDue={nextDue}
                fmtEUR={fmtEUR}
                safeInvoiceTotal={safeInvoiceTotal}
            />
        ),
        "quick-links": (
            <DashboardQuickLinks
                hasAccounting={hasAccounting}
                invoices={invoices}
                openInvoices={openInvoices}
                overdueInvoices={overdueInvoices}
            />
        ),
    };

    return (
        <>
            <GuidedTour
                open={tourOpen}
                steps={tourSteps}
                tourId="dashboard_onboarding"
                onClose={handleTourClose}
                onComplete={handleTourComplete}
            />
            <DashboardHeader
                monthFilter={monthFilter}
                onMonthFilterChange={setMonthFilter}
                yearOptions={yearOptions}
                hasAccounting={hasAccounting}
                onRefresh={handleRefresh}
                onStartTour={startTour}
                tourOpen={tourOpen}
            />

            <Container maxWidth="lg" sx={{mt: 4, pb: 6}}>
                <DashboardGrid layouts={STATIC_LAYOUTS} items={items} loading={loading} err={err}/>

                <Box sx={{mt: 4, display: {xs: "none", md: "block"}}}>
                    <FaqSearch/>
                </Box>

                <DashboardSnackbar
                    toast={toast}
                    onClose={() => setToast((current) => ({...current, open: false}))}
                />
            </Container>
        </>
    );
}
